import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, RotateCcwIcon } from 'lucide-react';
import { DeckLogoPlate } from '../components/deck/DeckLogo';
import { SlideCover } from '../components/deck/slides/SlideCover';
import { SlideMarketShift } from '../components/deck/slides/SlideMarketShift';
import { SlideFriction } from '../components/deck/slides/SlideFriction';
import { SlideCarRental } from '../components/deck/slides/SlideCarRental';
import { SlideTalabAsk } from '../components/deck/slides/SlideTalabAsk';
import { SlideSalesAgent } from '../components/deck/slides/SlideSalesAgent';
import { SlideOnboarding } from '../components/deck/slides/SlideOnboarding';
import { SlideBenefits } from '../components/deck/slides/SlideBenefits';
import { SlideGoToMarket } from '../components/deck/slides/SlideGoToMarket';
import { SlideClosing } from '../components/deck/slides/SlideClosing';
import { useScreenInit } from '../useScreenInit.js';
const STAGE_WIDTH = 1280;
const STAGE_HEIGHT = 720;
const EASE = [0.23, 1, 0.32, 1] as const;
interface SlideRenderProps {
  active: boolean;
  /** A slide calls this to take over the whole viewer (deck chrome hides). */
  onImmersiveChange?: (immersive: boolean) => void;
}
interface DeckSlide {
  id: string;
  label: string;
  render: (props: SlideRenderProps) => React.ReactNode;
}
const SLIDES: DeckSlide[] = [{
  id: 'cover',
  label: 'Cover',
  render: props => <SlideCover {...props} data-id="element-3844" data-mp="1f3f1tbydpmax" />
}, {
  id: 'shift',
  label: 'Market shift',
  render: props => <SlideMarketShift {...props} data-id="element-3845" data-mp="1k4umv40k3stb" />
}, {
  id: 'problem',
  label: 'The problem',
  render: props => <SlideFriction {...props} data-id="element-3846" data-mp="1p238xfotsgzu" />
}, {
  id: 'today',
  label: 'Traditional flow',
  render: props => <SlideCarRental {...props} data-id="element-3847" data-mp="3lwzm70kdxse2" />
}, {
  id: 'talab',
  label: 'The Talab experience',
  render: props => <SlideTalabAsk {...props} data-id="element-3848" data-mp="3qvkbw620gto9" />
}, {
  id: 'agent',
  label: 'AI sales agent',
  render: props => <SlideSalesAgent {...props} data-id="element-3849" data-mp="3iuuto2196bv0" />
}, {
  id: 'onboarding',
  label: 'Onboarding',
  render: props => <SlideOnboarding {...props} data-id="element-3850" data-mp="3ett1u69o1232" />
}, {
  id: 'benefits',
  label: 'Benefits',
  render: props => <SlideBenefits {...props} data-id="element-3851" data-mp="3staifyct591c" />
}, {
  id: 'gtm',
  label: 'Go-to-market',
  render: props => <SlideGoToMarket {...props} data-id="element-3852" data-mp="3nswlbbcqvji3" />
}, {
  id: 'closing',
  label: 'Closing',
  render: props => <SlideClosing {...props} data-id="element-3853" data-mp="5n1fk1ebqjkj" />
}];
export function Presentation() {
  const screenInit = useScreenInit();
  const [index, setIndex] = useState(typeof screenInit?.index === 'number' ? screenInit.index : 0);
  const [direction, setDirection] = useState(1);
  const [immersive, setImmersive] = useState(false);
  const [scale, setScale] = useState(1);
  const [viewport, setViewport] = useState(() => typeof window === 'undefined' ? {
    width: STAGE_WIDTH,
    height: STAGE_HEIGHT
  } : {
    width: window.innerWidth,
    height: window.innerHeight
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStart = useRef<{
    x: number;
    y: number;
  } | null>(null);

  /**
   * A 16:9 stage can never fill a portrait phone — it letterboxes down to a
   * strip. On portrait phones the deck is therefore rotated a quarter turn and
   * scaled to fill the screen edge to edge, the way a full-screen deck viewer
   * behaves: turn the phone and the slide occupies the whole display.
   */
  const isPhone = viewport.width < 700 || viewport.height < 500;
  const rotateStage = isPhone && viewport.height > viewport.width;
  const go = useCallback((next: number) => {
    const target = Math.min(Math.max(next, 0), SLIDES.length - 1);
    setDirection(target >= index ? 1 : -1);
    setImmersive(false);
    setIndex(target);
  }, [index]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (['ArrowRight', 'PageDown', ' '].includes(event.key)) {
        event.preventDefault();
        setDirection(1);
        setIndex(current => Math.min(current + 1, SLIDES.length - 1));
      } else if (['ArrowLeft', 'PageUp'].includes(event.key)) {
        event.preventDefault();
        setDirection(-1);
        setIndex(current => Math.max(current - 1, 0));
      } else if (event.key === 'Home') {
        setDirection(-1);
        setIndex(0);
      } else if (event.key === 'End') {
        setDirection(1);
        setIndex(SLIDES.length - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useLayoutEffect(() => {
    const measure = () => setViewport({
      width: window.innerWidth,
      height: window.innerHeight
    });
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);
  useLayoutEffect(() => {
    const fit = () => {
      const node = containerRef.current;
      if (!node) return;
      const x = node.clientWidth / STAGE_WIDTH;
      const y = node.clientHeight / STAGE_HEIGHT;
      // Always contain the 16:9 stage so no composition is ever cropped. In
      // immersive mode the viewer turns white, so any leftover margin reads as
      // page whitespace rather than a letterbox bar.
      setScale(Math.min(x, y) || 1);
    };
    fit();
    const observer = new ResizeObserver(fit);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isPhone, immersive]);
  const slide = SLIDES[index];

  // Accept either gesture axis so swiping works however the phone is held.
  const gestureHandlers = {
    onClick: () => go(index + 1),
    onTouchStart: (event: React.TouchEvent) => {
      touchStart.current = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    },
    onTouchEnd: (event: React.TouchEvent) => {
      const start = touchStart.current;
      if (!start) return;
      const deltaX = event.changedTouches[0].clientX - start.x;
      const deltaY = event.changedTouches[0].clientY - start.y;
      touchStart.current = null;
      const delta = Math.abs(deltaX) >= Math.abs(deltaY) ? deltaX : deltaY;
      if (Math.abs(delta) > 48) go(delta < 0 ? index + 1 : index - 1);
    }
  };
  const stage = <AnimatePresence initial={false} custom={direction} data-id="element-3854" data-mp="1q6wj635twpyg">
      <motion.section key={slide.id} className="absolute inset-0" custom={direction} initial={{
      opacity: 0,
      x: direction * 40
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: direction * -28
    }} transition={{
      duration: 0.3,
      ease: EASE
    }} aria-label={slide.label} data-id="element-3855" data-mp="12fr4hrbmk3pq">
        {slide.render({
        active: true,
        onImmersiveChange: setImmersive
      })}
      </motion.section>
    </AnimatePresence>;

  /* ---------------- Phone: edge-to-edge full-screen viewer ---------------- */
  if (isPhone) {
    const along = rotateStage ? viewport.height : viewport.width;
    const across = rotateStage ? viewport.width : viewport.height;
    const phoneScale = Math.min(along / STAGE_WIDTH, across / STAGE_HEIGHT);
    return <div className="fixed inset-0 bg-dark-navy overflow-hidden select-none" {...gestureHandlers} role="presentation" data-id="element-3856" data-mp="3qtprtm1unpew">
        <div className="absolute left-1/2 top-1/2 overflow-hidden bg-white" style={{
        width: STAGE_WIDTH,
        height: STAGE_HEIGHT,
        transform: `translate(-50%, -50%) ${rotateStage ? 'rotate(90deg) ' : ''}scale(${phoneScale})`,
        transformOrigin: 'center center',
        transition: 'transform 700ms cubic-bezier(0.23, 1, 0.32, 1)'
      }} data-id="element-3857" data-mp="3vtkibohpqczf">
          {stage}

          {/* Minimal in-stage chrome so it rotates with the slide */}
          <motion.div className="absolute top-0 left-0 right-0 h-1.5 bg-dark-navy/10 z-50" animate={{
          opacity: immersive ? 0 : 1
        }} transition={{
          duration: 0.6,
          ease: EASE
        }} data-id="element-3858" data-mp="2y7ddcdhhyimj">
            <motion.div className="h-full bg-gradient-to-r from-primary-indigo to-ai-purple" animate={{
            width: `${(index + 1) / SLIDES.length * 100}%`
          }} transition={{
            duration: 0.4,
            ease: EASE
          }} data-id="element-3859" data-mp="2ferihvgfvzus" />
          </motion.div>
          <motion.div className="absolute bottom-5 right-8 z-50 flex items-center gap-3 px-4 py-2 rounded-full bg-dark-navy/85 backdrop-blur-sm" animate={{
          opacity: immersive ? 0 : 1,
          y: immersive ? 10 : 0
        }} transition={{
          duration: 0.6,
          ease: EASE
        }} data-id="element-3860" data-mp="26g61i4qm8sw4">
            <span className="font-display font-bold text-[20px] text-white tabular-nums" data-id="element-3861" data-mp="2tbwlfmkuwilt">
              {String(index + 1).padStart(2, '0')}
              <span className="text-white/40" data-id="element-3862" data-mp="3lvllvinwj6af"> / {String(SLIDES.length).padStart(2, '0')}</span>
            </span>
            <span className="text-[16px] text-white/55" data-id="element-3863" data-mp="3h31yxv3bqysy">Tap to advance</span>
          </motion.div>
        </div>

        <AnimatePresence data-id="element-3864" data-mp="13n1vb1dj5w7w">
          {index === 0 && rotateStage && <motion.div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 backdrop-blur-sm pointer-events-none" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.4,
          delay: 1
        }} data-id="element-3865" data-mp="18lbq3avceusz">
              <RotateCcwIcon className="w-3.5 h-3.5 text-white/70" data-id="element-3866" data-mp="1rimpqwkvzq8y" />
              <span className="text-[11px] text-white/70" data-id="element-3867" data-mp="1rgthxdyjezzd">Turn your phone to full screen</span>
            </motion.div>}
        </AnimatePresence>
      </div>;
  }

  /* ---------------- Landscape / tablet / desktop ---------------- */
  return <div className={`app-fill-screen relative flex flex-col h-screen overflow-hidden transition-colors duration-500 ${immersive ? 'bg-white' : 'bg-dark-navy'}`} data-id="element-3868" data-mp="25bm7z6c1zyk7">
      <motion.div className="w-full bg-white/10 shrink-0 overflow-hidden" animate={{
      height: immersive ? 0 : 3,
      opacity: immersive ? 0 : 1
    }} transition={{
      duration: 0.7,
      ease: EASE
    }} data-id="element-3869" data-mp="15u8cppttpzx8">
        <motion.div className="h-full bg-gradient-to-r from-primary-indigo to-ai-purple" animate={{
        width: `${(index + 1) / SLIDES.length * 100}%`
      }} transition={{
        duration: 0.4,
        ease: EASE
      }} data-id="element-3870" data-mp="1et27rbriai8o" />
      </motion.div>

      <div ref={containerRef} className={`flex-1 min-h-0 flex items-center justify-center select-none ${immersive ? 'p-0' : 'p-0 sm:p-4 lg:p-6'}`} {...gestureHandlers} role="presentation" data-id="element-3871" data-mp="1tihv0hrtw5jm">
        <div className={`relative overflow-hidden bg-white ${immersive ? '' : 'sm:rounded-2xl'}`} style={{
        width: STAGE_WIDTH,
        height: STAGE_HEIGHT,
        transform: `scale(${scale})`,
        transformOrigin: 'center center',
        transition: 'transform 700ms cubic-bezier(0.23, 1, 0.32, 1)',
        boxShadow: immersive ? 'none' : '0 50px 120px -40px rgba(0,0,0,0.8)'
      }} data-id="element-3872" data-mp="22iwi9mocrupr">
          {stage}
        </div>
      </div>

      <motion.div className="shrink-0 overflow-hidden" animate={{
      height: immersive ? 0 : 'auto',
      opacity: immersive ? 0 : 1
    }} transition={{
      duration: 0.7,
      ease: EASE
    }} data-id="element-3873" data-mp="3n20cgsfuawfe">
        <div className="flex items-center gap-3 sm:gap-6 px-4 py-2.5 sm:px-7 sm:py-4 border-t border-white/10" data-id="element-3874" data-mp="jjyuhqgns5bi">
        <div className="hidden sm:flex items-center gap-2.5 shrink-0 lg:w-[260px]" data-id="element-3875" data-mp="ejkjq0noah9s">
          <DeckLogoPlate tone="dark" data-id="element-3876" data-mp="ai8tpu0vzu12" />
          <span className="hidden lg:block text-[10.5px] uppercase tracking-[0.18em] text-white/40 leading-tight" data-id="element-3877" data-mp="5l8y6d1ny9o7">
            Merchant · Partner · Investor Overview
          </span>
        </div>

        <div className="hidden md:flex flex-1 items-center justify-center gap-1.5" data-id="element-3878" data-mp="2qbhj99inmvf3">
          {SLIDES.map((item, slideIndex) => <button key={item.id} type="button" onClick={() => go(slideIndex)} aria-label={`Go to slide ${slideIndex + 1}: ${item.label}`} aria-current={slideIndex === index} className="group relative py-2 px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-electric-cyan rounded" data-id="element-3879" data-mp="2rtev22nqsvpm" data-mpi={slideIndex}>
              <span className={`block h-[3px] rounded-full transition-[width,background-color] duration-200 ease-out ${slideIndex === index ? 'w-9 bg-white' : 'w-4 bg-white/25 group-hover:bg-white/50'}`} data-id="element-3880" data-mp="291lxduxqambk" />
            </button>)}
        </div>

        <div className="flex flex-1 md:flex-none items-center justify-end gap-2.5 sm:gap-3 lg:w-[260px]" data-id="element-3881" data-mp="240g3htsc0t0g">
          <Link to="/app" className="hidden md:block text-[10.5px] font-medium uppercase tracking-[0.14em] text-white/45 transition-colors duration-150 hover:text-white whitespace-nowrap" data-id="element-3882" data-mp="1v24h63yi0hdt">
            Open Talab →
          </Link>
          <span className="md:hidden flex-1 min-w-0 truncate text-left text-[10.5px] text-white/40" data-id="element-3883" data-mp="16j2g6i2gmm24">
            {slide.label}
          </span>
          <span className="font-display font-bold text-[12px] text-white/70 tabular-nums whitespace-nowrap" data-id="element-3884" data-mp="1gfnll4ie5z6h">
            {String(index + 1).padStart(2, '0')}
            <span className="text-white/30" data-id="element-3885" data-mp="1udc9ymbo3oga"> / {String(SLIDES.length).padStart(2, '0')}</span>
          </span>
          <div className="flex items-center gap-1.5" data-id="element-3886" data-mp="1pdzljx55e9bl">
            <button type="button" onClick={event => {
              event.stopPropagation();
              go(index - 1);
            }} disabled={index === 0} aria-label="Previous slide" className="w-8 h-8 rounded-lg border border-white/15 text-white/70 flex items-center justify-center transition-colors duration-150 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-transparent" data-id="element-3887" data-mp="243yftotl10ls">
              <ChevronLeftIcon className="w-4 h-4" data-id="element-3888" data-mp="bfz49g93ppj0" />
            </button>
            <button type="button" onClick={event => {
              event.stopPropagation();
              go(index + 1);
            }} disabled={index === SLIDES.length - 1} aria-label="Next slide" className="w-8 h-8 rounded-lg bg-white text-dark-navy flex items-center justify-center transition-opacity duration-150 hover:opacity-90 disabled:opacity-30" data-id="element-3889" data-mp="be5y022eh7bj">
              <ChevronRightIcon className="w-4 h-4" data-id="element-3890" data-mp="3snkq5rdlnjxs" />
            </button>
          </div>
        </div>
        </div>
      </motion.div>

      <AnimatePresence data-id="element-3891" data-mp="1g88lb8t14hc">
        {index === 0 && <motion.p className="absolute bottom-[58px] sm:bottom-24 left-1/2 -translate-x-1/2 w-full max-w-[320px] sm:max-w-none px-3 text-center text-[10.5px] sm:text-[11px] text-white/45 pointer-events-none" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.4,
        delay: 1.4
      }} data-id="element-3892" data-mp="17z6h1jpwjfyu">
            <span className="sm:hidden" data-id="element-3893" data-mp="1datqzlw6fioz">Tap or swipe to advance</span>
            <span className="hidden sm:inline" data-id="element-3894" data-mp="19a03gtmnc5wm">Use → or Space to advance</span>
          </motion.p>}
      </AnimatePresence>
    </div>;
}